import { Edge, Node } from '@xyflow/react';

import { StepType } from '@frontend/types/workflow-agent';

import { generateNodeId } from '../services/workflow-id-utils';

export function isSpecializedToConditionConnection(sourceNode: Node, targetNode: Node): boolean {
  const sourceData = sourceNode.data as Record<string, unknown>;
  return sourceData.type === StepType.SPECIALIZED && targetNode.type === 'condition';
}

/**
 * Evaluates if a node needs type replacement based on its condition connections
 * Handles bidirectional transformations:
 * - Specialized + condition connections → Conditional
 * - Conditional + no condition connections → Specialized
 * Returns replacement info if needed, null if no change required
 */
export function evaluateAndReplaceNodeType(
  targetNodeId: string,
  nodes: Node[],
  edges: Edge[]
): { shouldReplace: boolean; newNode?: Node; newNodeId?: string; updatedEdges?: Edge[] } {
  const targetNode = nodes.find(n => n.id === targetNodeId);
  if (!targetNode) {
    return { shouldReplace: false };
  }

  const nodeData = targetNode.data as Record<string, unknown>;
  const currentType = nodeData.type;

  // Only handle specialized and conditional nodes
  if (currentType !== StepType.SPECIALIZED && currentType !== StepType.CONDITIONAL) {
    return { shouldReplace: false };
  }

  // Count condition connections from this node (this node -> condition nodes)
  const conditionConnections = edges.filter(edge => {
    const targetNode = nodes.find(n => n.id === edge.target);
    return edge.source === targetNodeId && targetNode && targetNode.type === 'condition';
  });

  const hasConditions = conditionConnections.length > 0;

  // Determine if transformation is needed
  const shouldTransform =
    (currentType === StepType.SPECIALIZED && hasConditions) || // Specialized → Conditional
    (currentType === StepType.CONDITIONAL && !hasConditions); // Conditional → Specialized

  if (!shouldTransform) {
    return { shouldReplace: false };
  }

  const newNodeId = generateNodeId();
  let newNodeData: Record<string, unknown>;
  let newNodeType: string;

  if (currentType === StepType.SPECIALIZED) {
    // Convert specialized to conditional
    newNodeData = {
      ...nodeData,
      type: StepType.CONDITIONAL,
      step_id: newNodeId,
      evaluator_agent_id: nodeData.agent_id || nodeData.step_id || targetNodeId,
      conditions: [], // Will be populated by graph-to-workflow logic
    };
    delete newNodeData.agent_id; // Remove specialized-specific field
    newNodeType = 'conditional';
  } else {
    // Convert conditional back to specialized
    newNodeData = {
      ...nodeData,
      type: StepType.SPECIALIZED,
      step_id: newNodeId,
      agent_id: nodeData.evaluator_agent_id || nodeData.step_id || targetNodeId,
    };
    delete newNodeData.evaluator_agent_id; // Remove conditional-specific field
    delete newNodeData.conditions; // Remove conditional-specific field
    newNodeType = 'specialized';
  }

  const newNode: Node = {
    ...targetNode,
    id: newNodeId,
    type: newNodeType,
    data: newNodeData,
  };

  // Update all edges that reference the old node ID
  const updatedEdges = edges.map(edge => {
    if (edge.source === targetNodeId) {
      return { ...edge, source: newNodeId };
    }
    if (edge.target === targetNodeId) {
      return { ...edge, target: newNodeId };
    }
    return edge;
  });

  return {
    shouldReplace: true,
    newNode,
    newNodeId,
    updatedEdges,
  };
}
